const TAIPEI_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Taipei",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

const ZH_DATE_FORMATTER = new Intl.DateTimeFormat("zh-TW", {
  timeZone: "Asia/Taipei",
  month: "numeric",
  day: "numeric",
  weekday: "short"
});

function ymd(year, month, day) {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function dateKey(value, endExclusive = false) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (endExclusive) date.setTime(date.getTime() - 1);
  return TAIPEI_DATE_FORMATTER.format(date);
}

function fromKey(value) {
  return new Date(`${value}T00:00:00.000Z`);
}

function addDays(value, days) {
  const date = typeof value === "string" ? fromKey(value) : new Date(value);
  date.setUTCDate(date.getUTCDate() + days);
  return ymd(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function startOfGrid(year, monthIndex) {
  const first = new Date(Date.UTC(year, monthIndex, 1));
  first.setUTCDate(first.getUTCDate() - first.getUTCDay());
  return ymd(first.getUTCFullYear(), first.getUTCMonth() + 1, first.getUTCDate());
}

function eventsOnDate(events, key) {
  return events.filter((event) => {
    const start = dateKey(event.start);
    const end = dateKey(event.end, !event.allDay);
    return start <= key && key <= (event.allDay ? addDays(event.end, -1) : end);
  });
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

class GeownClinicCalendar extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === "true") return;
    this.dataset.ready = "true";
    this.current = new Date();
    this.current = new Date(Date.UTC(this.current.getFullYear(), this.current.getMonth(), 1));
    this.agendaDays = 14;
    this.events = [];
    this.renderShell();
    this.load();
  }

  renderShell() {
    const title = this.dataset.title || "門診時間表";
    const bookingUrl = this.dataset.bookingUrl || "/pages/contact";
    const note = this.dataset.note || "門診可能因醫師行程調整，來訪前請再次確認。";
    this.innerHTML = `
      <section class="gcc-card" aria-labelledby="gcc-title-${this.id || "calendar"}">
        <header class="gcc-header">
          <div>
            <p class="gcc-eyebrow">GEO AESTHETIC CLINIC</p>
            <h2 class="gcc-title" id="gcc-title-${this.id || "calendar"}"></h2>
          </div>
          <div class="gcc-nav" aria-label="月份切換">
            <button type="button" data-action="previous" aria-label="上一個月">‹</button>
            <button type="button" data-action="today">本月</button>
            <button type="button" data-action="next" aria-label="下一個月">›</button>
          </div>
        </header>
        <div class="gcc-month-label" aria-live="polite"></div>
        <div class="gcc-loading" role="status">正在載入門診資料…</div>
        <div class="gcc-desktop" hidden>
          <div class="gcc-weekdays" aria-hidden="true"></div>
          <div class="gcc-grid" role="grid"></div>
        </div>
        <div class="gcc-mobile" hidden>
          <div class="gcc-agenda"></div>
          <button class="gcc-more" type="button" data-action="more">載入更多日期</button>
        </div>
        <div class="gcc-status" aria-live="polite"></div>
        <footer class="gcc-footer">
          <p class="gcc-note"></p>
          <a class="gcc-booking" rel="noopener" data-booking>透過官方 LINE 確認門診</a>
        </footer>
      </section>`;

    this.querySelector(".gcc-title").textContent = title;
    this.querySelector(".gcc-note").textContent = note;
    this.querySelector("[data-booking]").href = bookingUrl;
    this.querySelector('[data-action="previous"]').addEventListener("click", () => this.changeMonth(-1));
    this.querySelector('[data-action="today"]').addEventListener("click", () => this.goToday());
    this.querySelector('[data-action="next"]').addEventListener("click", () => this.changeMonth(1));
    this.querySelector('[data-action="more"]').addEventListener("click", () => {
      this.agendaDays = 42;
      this.renderAgenda();
    });
  }

  changeMonth(delta) {
    this.current = new Date(Date.UTC(this.current.getUTCFullYear(), this.current.getUTCMonth() + delta, 1));
    this.agendaDays = 14;
    this.load();
  }

  goToday() {
    const now = new Date();
    this.current = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
    this.agendaDays = 14;
    this.load();
  }

  async load() {
    const loading = this.querySelector(".gcc-loading");
    const desktop = this.querySelector(".gcc-desktop");
    const mobile = this.querySelector(".gcc-mobile");
    loading.hidden = false;
    loading.textContent = "正在載入門診資料…";
    desktop.hidden = true;
    mobile.hidden = true;

    const year = this.current.getUTCFullYear();
    const month = this.current.getUTCMonth();
    const start = startOfGrid(year, month);
    const end = addDays(start, 42);
    const apiBase = (this.dataset.apiBase || "").replace(/\/$/, "");
    const branch = this.dataset.branch;
    const url = `${apiBase}/api/calendar/${encodeURIComponent(branch)}?start=${start}&end=${end}`;

    try {
      const response = await fetch(url, { headers: { accept: "application/json" } });
      if (!response.ok) throw new Error("calendar unavailable");
      const payload = await response.json();
      if (!Array.isArray(payload.events)) throw new Error("invalid calendar response");
      this.events = payload.events;
      this.rangeStart = start;
      this.renderMonth();
      this.renderAgenda();
      const updated = payload.fetchedAt ? new Date(payload.fetchedAt).toLocaleString("zh-TW", { timeZone: "Asia/Taipei", hour12: false }) : "";
      this.querySelector(".gcc-status").textContent = payload.stale
        ? `目前顯示最近一次成功資料${updated ? `（${updated}）` : ""}，請於預約時再次確認。`
        : updated ? `資料更新：${updated}` : "";
      loading.hidden = true;
      desktop.hidden = false;
      mobile.hidden = false;
    } catch {
      loading.textContent = "門診資料暫時無法載入，請透過官方 LINE 確認最新安排。";
      this.querySelector(".gcc-status").textContent = "";
    }
  }

  renderMonth() {
    const year = this.current.getUTCFullYear();
    const month = this.current.getUTCMonth();
    this.querySelector(".gcc-month-label").textContent = `${year} 年 ${month + 1} 月`;
    const weekdays = this.querySelector(".gcc-weekdays");
    weekdays.replaceChildren(...["日", "一", "二", "三", "四", "五", "六"].map((day) => element("div", "", `週${day}`)));

    const grid = this.querySelector(".gcc-grid");
    const today = TAIPEI_DATE_FORMATTER.format(new Date());
    const cells = [];
    for (let index = 0; index < 42; index += 1) {
      const key = addDays(this.rangeStart, index);
      const date = fromKey(key);
      const cell = element("div", "gcc-day");
      cell.setAttribute("role", "gridcell");
      cell.setAttribute("aria-label", ZH_DATE_FORMATTER.format(date));
      if (date.getUTCMonth() !== month) cell.classList.add("is-other-month");
      if (key === today) cell.classList.add("is-today");
      cell.append(element("span", "gcc-day-number", String(date.getUTCDate())));
      for (const event of eventsOnDate(this.events, key)) {
        cell.append(element("span", `gcc-event is-${event.kind === "closed" ? "closed" : "clinic"}`, event.title));
      }
      cells.push(cell);
    }
    grid.replaceChildren(...cells);
  }

  renderAgenda() {
    const agenda = this.querySelector(".gcc-agenda");
    const now = new Date();
    const currentMonthIsNow = this.current.getUTCFullYear() === now.getFullYear() && this.current.getUTCMonth() === now.getMonth();
    const firstKey = currentMonthIsNow
      ? TAIPEI_DATE_FORMATTER.format(now)
      : ymd(this.current.getUTCFullYear(), this.current.getUTCMonth() + 1, 1);
    const rows = [];
    for (let index = 0; index < this.agendaDays; index += 1) {
      const key = addDays(firstKey, index);
      const dayEvents = eventsOnDate(this.events, key);
      if (!dayEvents.length) continue;
      const row = element("section", "gcc-agenda-row");
      row.append(element("h3", "gcc-agenda-date", ZH_DATE_FORMATTER.format(fromKey(key))));
      const list = element("ul", "gcc-agenda-events");
      for (const event of dayEvents) {
        const item = element("li", event.kind === "closed" ? "is-closed" : "is-clinic", event.title);
        list.append(item);
      }
      row.append(list);
      rows.push(row);
    }
    agenda.replaceChildren(...(rows.length ? rows : [element("p", "gcc-empty", "目前查詢期間沒有門診資料。")]));
    this.querySelector(".gcc-more").hidden = this.agendaDays >= 42;
  }
}

if (!customElements.get("geown-clinic-calendar")) {
  customElements.define("geown-clinic-calendar", GeownClinicCalendar);
}

export { addDays, dateKey, eventsOnDate, startOfGrid };
